import { useState, useEffect, useRef, useCallback } from "react";

export interface DropdownItem<K extends string = string> {
  key: K;
  label?: string;
}

export const useDropdown = <K extends string = string>(
  items: DropdownItem<K>[],
  initialSelected: K,
  onSelect?: (key: K) => void
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<K>(initialSelected);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const handleSelect = useCallback(
    (key: K) => {
      setSelectedKey(key);
      setIsOpen(false);
      if (onSelect) onSelect(key);
    },
    [onSelect]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        const currentIndex = items.findIndex((item) => item.key === selectedKey);
        const nextIndex = (currentIndex + 1) % items.length;
        setSelectedKey(items[nextIndex].key);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        const currentIndex = items.findIndex((item) => item.key === selectedKey);
        const prevIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
        setSelectedKey(items[prevIndex].key);
      } else if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, selectedKey, items]);

  return {
    isOpen,
    setIsOpen,
    selectedKey,
    setSelectedKey,
    dropdownRef,
    handleSelect,
  } as const;
};
